import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { loadAppConfig } from "@/lib/appConfig";
import type { AppConfig } from "@/lib/appConfig";
import { acquireApiToken, getActiveAccount, initMsal, loginRedirect, logoutMsal } from "./msal";

/** Shape of the shared /api/auth/me endpoint (Tts.Identity.AspNetCore MeResponse). */
export interface Me {
  id: string;
  displayName: string;
  email: string;
  provider: string;
  roles: string[];
  mustChangePassword: boolean;
}

type AuthMode = "entra" | "local" | "none";

interface LocalSession {
  accessToken: string;
  refreshToken: string;
}

interface AuthState {
  ready: boolean;
  config: AppConfig | null;
  mode: AuthMode;
  me: Me | null;
  startSsoLogin: () => Promise<void>;
  loginLocal: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const AuthCtx = createContext<AuthState | null>(null);

const LOCAL_SESSION_KEY = "tts-portal.session";

function readLocalSession(): LocalSession | null {
  const raw = sessionStorage.getItem(LOCAL_SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as LocalSession;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [mode, setMode] = useState<AuthMode>("none");
  const [me, setMe] = useState<Me | null>(null);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (mode === "entra") return acquireApiToken();
    if (mode === "local") return readLocalSession()?.accessToken ?? null;
    return null;
  }, [mode]);

  const fetchMe = useCallback(async (token: string): Promise<Me | null> => {
    const res = await fetch("/api/auth/me", { headers: { Authorization: `Bearer ${token}` } });
    return res.ok ? ((await res.json()) as Me) : null;
  }, []);

  useEffect(() => {
    void (async () => {
      const cfg = await loadAppConfig();
      setConfig(cfg);
      await initMsal(cfg);

      if (getActiveAccount()) {
        const token = await acquireApiToken();
        if (token) {
          setMe(await fetchMe(token));
          setMode("entra");
          setReady(true);
          return;
        }
      }

      const local = readLocalSession();
      if (local) {
        const profile = await fetchMe(local.accessToken);
        if (profile) {
          setMe(profile);
          setMode("local");
        } else {
          sessionStorage.removeItem(LOCAL_SESSION_KEY);
        }
      }
      setReady(true);
    })();
  }, [fetchMe]);

  const startSsoLogin = useCallback(async () => {
    await loginRedirect();
  }, []);

  const loginLocal = useCallback(
    async (email: string, password: string): Promise<boolean> => {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) return false;
      const body = (await res.json()) as { accessToken: string; refreshToken: string };
      sessionStorage.setItem(
        LOCAL_SESSION_KEY,
        JSON.stringify({ accessToken: body.accessToken, refreshToken: body.refreshToken }),
      );
      setMe(await fetchMe(body.accessToken));
      setMode("local");
      return true;
    },
    [fetchMe],
  );

  const logout = useCallback(async () => {
    if (mode === "local") {
      const session = readLocalSession();
      if (session) {
        await fetch("/api/auth/logout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: session.refreshToken }),
        });
      }
      sessionStorage.removeItem(LOCAL_SESSION_KEY);
      setMe(null);
      setMode("none");
      return;
    }
    if (mode === "entra") await logoutMsal();
  }, [mode]);

  const value = useMemo(
    () => ({ ready, config, mode, me, startSsoLogin, loginLocal, logout, getToken }),
    [ready, config, mode, me, startSsoLogin, loginLocal, logout, getToken],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
